import React from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export default function MockOrderDetailsDialog({ open, order, onClose, onShowRequisition }) {
  const [showPayment, setShowPayment] = React.useState(false);
  const [paymentChoice, setPaymentChoice] = React.useState('');
  const [paymentMethod, setPaymentMethod] = React.useState('email');
  const [paymentEmail, setPaymentEmail] = React.useState('');
  const [paymentTextNumber, setPaymentTextNumber] = React.useState('');
  const [paymentQrFullscreen, setPaymentQrFullscreen] = React.useState(false);
  const [paymentCodeScanned, setPaymentCodeScanned] = React.useState(false);
  const qrFullscreenRef = React.useRef(null);

  React.useEffect(() => {
    if (open) {
      setShowPayment(false);
      setPaymentChoice('');
      setPaymentMethod('email');
      setPaymentEmail('');
      setPaymentTextNumber('');
      setPaymentQrFullscreen(false);
      setPaymentCodeScanned(false);
    }
  }, [open]);

  if (!order) {
    return null;
  }

  const handleDialogClose = (_, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  const patientEmailOptions = [order.patientEmail].filter(Boolean);
  const patientTextOptions = [order.patientPhone].filter(Boolean);
  const paymentCloseEnabled =
    paymentChoice === 'qr'
      ? paymentCodeScanned
      : paymentChoice === 'send-link'
        ? paymentMethod === 'email'
          ? Boolean(paymentEmail)
          : Boolean(paymentTextNumber)
        : false;

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>{`Order ${order.id}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Order Details
              </Typography>
              <Typography variant="body2">{order.patient}</Typography>
              <Typography variant="body2" color="text.secondary">{order.test}</Typography>
              <Typography variant="body2" color="text.secondary">{`Status: ${order.status}`}</Typography>
              <Typography variant="body2" color="text.secondary">{`Delivery: ${order.delivery}`}</Typography>
              <Typography variant="body2" color="text.secondary">{`Created: ${order.createdAt}`}</Typography>
            </Box>
          </Card>

          {!showPayment ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button variant="outlined" onClick={() => onShowRequisition?.(order)}>
                Requisition Form
              </Button>
              <Button variant="contained" onClick={() => setShowPayment(true)}>
                Show Payment Options
              </Button>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body1" color="text.secondary">
                A payment link was created. The payment must be completed before the kit will be shipped.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                }}
              >
                <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2 }}>
                  <Box sx={{ p: 2 }}>
                    <FormControlLabel
                      value="qr"
                      control={<Radio checked={paymentChoice === 'qr'} onChange={() => setPaymentChoice('qr')} />}
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Payment QR Code
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Third party can scan code to make payment.
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', m: 0 }}
                    />
                    {paymentChoice === 'qr' && (
                      <>
                        <Box
                          ref={qrFullscreenRef}
                          sx={{
                            mt: 1.5,
                            position: paymentQrFullscreen ? 'fixed' : 'relative',
                            inset: paymentQrFullscreen ? 0 : 'auto',
                            zIndex: paymentQrFullscreen ? 9999 : 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: paymentQrFullscreen ? '100vw' : 'auto',
                            height: paymentQrFullscreen ? '100vh' : 'auto',
                            backgroundColor: paymentQrFullscreen ? '#000' : 'transparent',
                            gap: 2,
                          }}
                        >
                          {paymentQrFullscreen && (
                            <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                              <IconButton
                                aria-label="Close full screen QR view"
                                onClick={() => {
                                  if (document.fullscreenElement) {
                                    document.exitFullscreen().catch(() => {});
                                  } else {
                                    setPaymentQrFullscreen(false);
                                  }
                                }}
                                sx={{ backgroundColor: 'rgba(255,255,255,0.85)' }}
                              >
                                X
                              </IconButton>
                            </Box>
                          )}
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent('https://pay.example.com/mock-order')}`}
                            alt="Payment QR Code"
                            style={{ width: 220, height: 220 }}
                          />
                        </Box>
                        {!paymentQrFullscreen && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                            <Button
                              variant="outlined"
                              onClick={async () => {
                                if (!qrFullscreenRef.current) return;
                                try {
                                  if (qrFullscreenRef.current.requestFullscreen) {
                                    await qrFullscreenRef.current.requestFullscreen();
                                  }
                                  setPaymentQrFullscreen(true);
                                } catch (err) {
                                  console.error('Failed to enter full screen QR view:', err);
                                }
                              }}
                            >
                              Full Screen
                            </Button>
                          </Box>
                        )}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={paymentCodeScanned}
                              onChange={(event) => setPaymentCodeScanned(event.target.checked)}
                            />
                          }
                          label="Code was scanned"
                          sx={{ mt: 1 }}
                        />
                      </>
                    )}
                  </Box>
                </Card>

                <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2 }}>
                  <Box sx={{ p: 2 }}>
                    <FormControlLabel
                      value="send-link"
                      control={<Radio checked={paymentChoice === 'send-link'} onChange={() => setPaymentChoice('send-link')} />}
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Send Payment Link
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Send the payment link directly to the payer.
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', m: 0 }}
                    />
                    {paymentChoice === 'send-link' && (
                      <>
                        <RadioGroup
                          value={paymentMethod}
                          onChange={(event) => setPaymentMethod(event.target.value)}
                          sx={{ mt: 1.5, mb: 1.5 }}
                        >
                          <FormControlLabel value="email" control={<Radio />} label="Email" />
                          <FormControlLabel value="text" control={<Radio />} label="Text Number" />
                        </RadioGroup>
                        {paymentMethod === 'email' ? (
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Email"
                            value={paymentEmail}
                            onChange={(event) => setPaymentEmail(event.target.value)}
                            sx={{ mb: 2 }}
                          >
                            {patientEmailOptions.length === 0 ? (
                              <MenuItem value="" disabled>
                                No patient emails available
                              </MenuItem>
                            ) : (
                              patientEmailOptions.map((email) => (
                                <MenuItem key={email} value={email}>
                                  {email}
                                </MenuItem>
                              ))
                            )}
                          </TextField>
                        ) : (
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Text Number"
                            value={paymentTextNumber}
                            onChange={(event) => setPaymentTextNumber(event.target.value)}
                            sx={{ mb: 2 }}
                          >
                            {patientTextOptions.length === 0 ? (
                              <MenuItem value="" disabled>
                                No patient text numbers available
                              </MenuItem>
                            ) : (
                              patientTextOptions.map((phone) => (
                                <MenuItem key={phone} value={phone}>
                                  {phone}
                                </MenuItem>
                              ))
                            )}
                          </TextField>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button variant="contained">Send</Button>
                        </Box>
                      </>
                    )}
                  </Box>
                </Card>
              </Box>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {showPayment && (
          <Button variant="contained" disabled={!paymentCloseEnabled}>
            Payment Complete
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
